import type {
  ButtonHTMLAttributes,
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react'
import { useEffect } from 'react'
import { avatarGradient, pct } from '../lib/format'
import './primitives.css'

// ─────────────────────────────────────────────────────────────────────────
// Primitive component library. Each is single-responsibility and defined once
// here; screens compose them. Styling lives in primitives.css.
// ─────────────────────────────────────────────────────────────────────────

// ── Button ─────────────────────────────────────────────────────────────────
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
  loading?: boolean
  icon?: ReactNode
}
export function Button({
  variant = 'secondary',
  size = 'md',
  block,
  loading,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    `btn--${variant}`,
    size === 'sm' && 'btn--sm',
    size === 'lg' && 'btn--lg',
    block && 'btn--block',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading ? <span className="btn__spinner" aria-hidden /> : icon}
      {children}
    </button>
  )
}

// ── Card ────────────────────────────────────────────────────────────────────
interface CardProps {
  inset?: boolean
  pad?: boolean
  hover?: boolean
  className?: string
  style?: CSSProperties
  onClick?: () => void
  children: ReactNode
}
export function Card({ inset, pad = true, hover, className = '', style, onClick, children }: CardProps) {
  const cls = [
    'card',
    inset && 'card--inset',
    pad && 'card--pad',
    hover && 'card--hover',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={cls} style={style} onClick={onClick}>
      {children}
    </div>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────
interface BadgeProps {
  color?: string
  bg?: string
  dot?: boolean
  live?: boolean
  children: ReactNode
  style?: CSSProperties
}
export function Badge({ color = 'var(--c-pale)', bg = 'rgba(155,143,255,0.1)', dot, live, children, style }: BadgeProps) {
  return (
    <span
      className="badge"
      style={{ color, background: bg, borderColor: `color-mix(in srgb, ${color} 30%, transparent)`, ...style }}
    >
      {dot && <span className={`badge__dot${live ? ' badge__dot--live' : ''}`} aria-hidden />}
      {children}
    </span>
  )
}

// ── Input ───────────────────────────────────────────────────────────────────
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
  ok?: boolean
  /** Render the part after @ as a static affix (used for work-email domain). */
  affix?: string
}
export function Input({ label, hint, error, ok, affix, className = '', id, ...rest }: InputProps) {
  const fieldCls = ['field', error && 'field--error', ok && 'field--ok'].filter(Boolean).join(' ')
  return (
    <div className={fieldCls}>
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {affix ? (
        <div className="input--prefix">
          <input id={id} className={className} {...rest} />
          <span className="input__affix">{affix}</span>
        </div>
      ) : (
        <input id={id} className={`input ${className}`} {...rest} />
      )}
      {error ? (
        <span className="field__error">
          <span aria-hidden>⚠</span> {error}
        </span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </div>
  )
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  options: { value: string; label: string }[]
}
export function Select({ label, options, id, className = '', ...rest }: SelectProps) {
  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      <select id={id} className={`select ${className}`} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  )
}

// ── Meter ───────────────────────────────────────────────────────────────────
interface MeterProps {
  label?: string
  used: number
  total: number
  unit?: string
  sm?: boolean
  foot?: ReactNode
  /** Force a tone; otherwise derived from how full it is. */
  tone?: 'normal' | 'warn' | 'over'
}
export function Meter({ label, used, total, unit, sm, foot, tone }: MeterProps) {
  const percentage = pct(used, total)
  const auto = used > total ? 'over' : percentage >= 85 ? 'warn' : 'normal'
  const t = tone ?? auto
  const fillCls = ['meter__fill', t === 'warn' && 'meter__fill--warn', t === 'over' && 'meter__fill--over']
    .filter(Boolean)
    .join(' ')
  return (
    <div className={`meter${sm ? ' meter--sm' : ''}`}>
      {label && (
        <div className="meter__head">
          <span className="meter__label">{label}</span>
          <span className="meter__value tnum">
            <b>{used}</b> / {total}
            {unit ? ` ${unit}` : ''}
          </span>
        </div>
      )}
      <div
        className="meter__track"
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={label}
      >
        <div className={fillCls} style={{ width: `${Math.min(100, percentage)}%` }} />
      </div>
      {foot && <div className="meter__foot">{foot}</div>}
    </div>
  )
}

// ── Avatar ──────────────────────────────────────────────────────────────────
export function Avatar({
  hue,
  initials,
  size = 40,
}: {
  hue: number
  initials: string
  size?: number
}) {
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        background: avatarGradient(hue),
        fontSize: size * 0.38,
      }}
      aria-hidden
    >
      {initials}
    </span>
  )
}

// ── Skeleton ────────────────────────────────────────────────────────────────
export function Skeleton({ w = '100%', h = 14, r, style }: { w?: number | string; h?: number | string; r?: number; style?: CSSProperties }) {
  return (
    <span
      className="skeleton"
      style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }}
      aria-hidden
    />
  )
}

// ── Modal ───────────────────────────────────────────────────────────────────
interface ModalProps {
  open: boolean
  onClose: () => void
  title?: ReactNode
  wide?: boolean
  children: ReactNode
}
export function Modal({ open, onClose, title, wide, children }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  return (
    <div className="modal__scrim" onClick={onClose}>
      <Card className={`modal${wide ? ' modal--wide' : ''}`} onClick={() => {}}>
        <div onClick={(e) => e.stopPropagation()}>
          {title && (
            <div className="modal__head">
              <h2 className="modal__title">{title}</h2>
              <button className="modal__close" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>
          )}
          {children}
        </div>
      </Card>
    </div>
  )
}
